import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plus, Lightbulb, Search, RefreshCw,
  ChevronDown, FileText, LayoutGrid, List, X,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageTracker } from '../../../components/PageTracker.tsx';
import CaptureIdeaModal from '../../../components/modals/CaptureIdeaModal.tsx';
import CaptureNoteModal from '../../../components/modals/CaptureNoteModal.tsx';
import PromotionModal from '../../../components/modals/PromotionModal.tsx';
import FunnelCard from '../../../components/funnel/FunnelCard.tsx';
import { ideasApi, type Idea, type IdeaStats, checkMultipleIdeaProjectStatus, type ProjectStatus } from '../../../services/ideasApi.ts';
import { funnelApi } from '../../../services/funnelApi.ts';
import { notesApi, type Note } from '../../../services/notesApi.ts';

// ── Design tokens (matches Workflows.jsx exactly) ────────────────────────────
const T = {
  page:        "var(--lyra-color-surface)",
  surface:     "var(--lyra-color-surface)",
  surface2:    "var(--lyra-color-surface-2)",
  surface3:    "var(--app-surface-3)",
  border:      "var(--lyra-color-border)",
  border2:     "var(--app-border-strong)",
  ink:         "var(--lyra-color-text)",
  ink2:        "var(--app-text-2)",
  ink3:        "var(--lyra-color-text-muted)",
  ink4:        "var(--app-text-3)",
  accent:      "var(--lyra-color-primary)",
  accentLight: "var(--app-primary-soft)",
  accentMid:   "var(--app-primary-soft-2)",
  accentDark:  "var(--app-primary-deep)",
  green:       "var(--lyra-color-success)",
  greenBg:     "var(--lyra-color-success-bg)",
  greenBd:     "var(--app-success-border)",
  red:         "var(--lyra-color-danger)",
  redBg:       "var(--lyra-color-danger-bg)",
  redBd:       "var(--app-danger-border)",
  amber:       "var(--lyra-color-warning)",
  amberBg:     "var(--lyra-color-warning-bg)",
  amberBd:     "var(--app-warning-border)",
};

const NOTE_COLORS: Record<string, { bg: string; border: string }> = {
  yellow: { bg: 'var(--lyra-color-surface-2)', border: 'var(--app-warning-border)' },
  blue:   { bg: 'var(--app-primary-soft)', border: 'var(--app-primary-soft-2)' },
  red:    { bg: 'var(--lyra-color-danger-bg)', border: 'var(--app-danger-border)' },
  green:  { bg: 'var(--lyra-color-success-bg)', border: 'var(--app-success-border)' },
};

const PRIORITY_WEIGHT: Record<string, number> = { High: 3, Medium: 2, Low: 1 };

const IdeaCapture: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [projectStatuses, setProjectStatuses] = useState<Record<string, ProjectStatus>>({});
  const [stats, setStats] = useState<IdeaStats>({
    total: 0,
    inProgress: 0,
    highPriority: 0,
    favorites: 0,
    completed: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [sortBy, setSortBy] = useState<'latest' | 'oldest' | 'priority'>('latest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [showCaptureDropdown, setShowCaptureDropdown] = useState(false);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [promotionItem, setPromotionItem] = useState<any>(null);
  const [editingIdea, setEditingIdea] = useState<Idea | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);

  // ── Notes (Ideas/Notes tab) ────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'ideas' | 'notes'>(
    (location.state as any)?.tab === 'notes' ? 'notes' : 'ideas'
  );
  const [notes, setNotes] = useState<Note[]>([]);
  const [noteSearchTerm, setNoteSearchTerm] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(true);
  const [showCaptureNoteModal, setShowCaptureNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState<Note | null>(null);

  // Fetch project statuses for all ideas
  const fetchProjectStatuses = useCallback(async () => {
    if (ideas.length > 0) {
      const ideaIds = ideas.map(idea => idea._id!).filter(Boolean);
      try {
        const statuses = await checkMultipleIdeaProjectStatus(ideaIds);
        setProjectStatuses(statuses);
      } catch (err) {
        console.error('Error fetching project statuses:', err);
      }
    }
  }, [ideas]);

  // Force refresh project statuses without depending on ideas array
  const forceRefreshProjectStatuses = useCallback(async () => {
    const currentIdeas = ideas;
    if (currentIdeas.length > 0) {
      const ideaIds = currentIdeas.map(idea => idea._id!).filter(Boolean);
      try {
        const statuses = await checkMultipleIdeaProjectStatus(ideaIds);
        setProjectStatuses(statuses);
      } catch (err) {
        console.error('Error fetching project statuses:', err);
      }
    }
  }, [ideas]);

  const fetchIdeas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {
        category: filterCategory !== 'All' ? filterCategory : undefined,
        status: filterStatus !== 'All' ? filterStatus : undefined,
        search: searchTerm.trim() || undefined
      };

      const fetchedIdeas = await ideasApi.fetchIdeas(filters);
      setIdeas(fetchedIdeas);

    } catch (err) {
      setError('Failed to fetch ideas');
      console.error('Error fetching ideas:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterCategory, filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const fetchedStats = await ideasApi.getStats();
      setStats(fetchedStats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const categories = await ideasApi.getCategories();
      setAvailableCategories(categories);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  const fetchNotes = useCallback(async () => {
    try {
      setLoadingNotes(true);
      const fetchedNotes = await notesApi.fetchNotes(noteSearchTerm.trim() || undefined);
      setNotes(fetchedNotes);
    } catch (err) {
      console.error('Error fetching notes:', err);
    } finally {
      setLoadingNotes(false);
    }
  }, [noteSearchTerm]);

  // Comprehensive refresh function
  const handleRefresh = useCallback(async () => {
    try {
      await Promise.all([
        fetchIdeas(),
        fetchStats(),
        forceRefreshProjectStatuses(),
        fetchNotes(),
      ]);
    } catch (err) {
      console.error('Error during refresh:', err);
    }
  }, [fetchIdeas, fetchStats, forceRefreshProjectStatuses, fetchNotes]);

  useEffect(() => {
    fetchIdeas();
    fetchStats();
    fetchCategories();
  }, [fetchIdeas, fetchStats, fetchCategories]);

  useEffect(() => {
    // Re-fetch ideas when filters change
    fetchIdeas();
  }, [fetchIdeas]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  useEffect(() => {
    // Fetch project statuses when ideas change
    fetchProjectStatuses();
  }, [fetchProjectStatuses]);

  useEffect(() => {
    // Refresh project statuses when window gains focus (user returns from another page)
    const handleFocus = () => {
      fetchProjectStatuses();
    };

    // Also add visibility change listener for better detection
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh project statuses
        fetchProjectStatuses();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchProjectStatuses]);

  useEffect(() => {
    // Force refresh project statuses when returning from navigation
    // This handles the case where user creates a project from an idea and comes back
    if (location.state?.refreshProjectStatuses) {
      forceRefreshProjectStatuses();
      // Clear the state to prevent unnecessary re-runs
      window.history.replaceState({}, document.title);
    }
  }, [location.state, forceRefreshProjectStatuses]);

  const categories = ['All', ...availableCategories];
  const statuses = ['All', 'New', 'In Progress', 'Researching', 'On Hold', 'Completed'];
  const priorities = ['All', 'High', 'Medium', 'Low'];

  const toggleFavorite = async (ideaId: string) => {
    try {
      const updatedIdea = await ideasApi.toggleFavorite(ideaId);
      setIdeas(ideas.map(idea =>
        idea._id === ideaId ? updatedIdea : idea
      ));
      // Update stats after toggle
      fetchStats();
    } catch (err) {
      console.error('Error toggling favorite:', err);
      setError('Failed to update favorite status');
    }
  };

  const handleAddIdea = async (ideaData: any) => {
    try {
      if (editingIdea) {
        // Update existing idea
        const updatedIdea = await ideasApi.updateIdea(editingIdea._id!, ideaData);
        setIdeas(ideas.map(idea =>
          idea._id === editingIdea._id ? updatedIdea : idea
        ));
        setEditingIdea(null);
      } else {
        // Create new idea
        const newIdea = await ideasApi.createIdea(ideaData);
        setIdeas([newIdea, ...ideas]);
      }
      // Update stats and categories after adding/updating
      fetchStats();
      fetchCategories();
      setShowCaptureModal(false);
    } catch (err) {
      console.error('Error saving idea:', err);
      setError('Failed to save idea');
    }
  };

  const handleEditIdea = (ideaId: string) => {
    const idea = ideas.find(i => i._id === ideaId);
    if (idea) {
      setEditingIdea(idea);
      setShowCaptureModal(true);
    }
  };

  const handleDeleteIdea = async (ideaId: string) => {
    if (!window.confirm('Are you sure you want to delete this idea?')) {
      return;
    }

    try {
      await ideasApi.deleteIdea(ideaId);
      setIdeas(ideas.filter(idea => idea._id !== ideaId));
      // Update stats after deletion
      fetchStats();
      fetchCategories();
    } catch (err) {
      console.error('Error deleting idea:', err);
      setError('Failed to delete idea');
    }
  };

  const handlePromoteIdea = (ideaId: string, fromStage: string, toStage: string) => {
    const idea = ideas.find(i => i._id === ideaId);
    if (idea && toStage === 'project') {
      // Navigate to personal projects page with idea data
      navigate('/tools/project-management/personal-projects', {
        state: {
          openModal: true,
          ideaData: {
            id: ideaId,
            title: idea.title,
            description: idea.description,
            category: idea.category,
            tags: idea.tags,
            priority: idea.priority,
            sourceIdeaId: ideaId // Include this for linking
          }
        }
      });
    }
  };

  const handleGoToProject = (ideaId: string, projectId: string) => {
    // Navigate to personal projects page and open the project modal with existing data
    navigate('/tools/project-management/personal-projects', {
      state: {
        openProjectModal: true,
        projectId: projectId,
        editMode: true
      }
    });
  };

  const handleConfirmPromotion = async (promotionData: any) => {
    try {
      // Call the funnel API to promote the idea to project
      const result = await funnelApi.promoteItem(promotionData);

      if (result.success) {
        setShowPromotionModal(false);
        setPromotionItem(null);

        // Refresh data after promotion
        await Promise.all([
          fetchIdeas(),
          fetchStats(),
        ]);

        setError(null);
      }
    } catch (error) {
      console.error('Error promoting idea:', error);
      setError(`Failed to promote idea: ${error.message}`);
    }
  };

  const handleDeleteIdeaOnly = async (ideaId: string) => {
    if (!window.confirm('Delete only this idea?')) return;
    try {
      await ideasApi.deleteIdea(ideaId);
      setIdeas(ideas.filter(idea => idea._id !== ideaId));
      fetchStats();
      fetchCategories();
    } catch (err) {
      console.error('Error deleting idea:', err);
      setError('Failed to delete idea');
    }
  };

  const handleDeleteIdeaAndProjects = async (ideaId: string) => {
    if (!window.confirm('Delete this idea and all associated projects?')) return;
    try {
      await ideasApi.deleteIdeaWithProjects(ideaId);
      setIdeas(ideas.filter(idea => idea._id !== ideaId));
      fetchStats();
      fetchCategories();
    } catch (err) {
      console.error('Error deleting idea & projects:', err);
      setError('Failed to delete idea & projects');
    }
  };

  const handleDeleteEverything = async (ideaId: string) => {
    if (!window.confirm('Delete this idea, all projects, and all related tasks?')) return;
    try {
      await ideasApi.deleteIdeaWithEverything(ideaId);
      setIdeas(ideas.filter(idea => idea._id !== ideaId));
      fetchStats();
      fetchCategories();
    } catch (err) {
      console.error('Error deleting everything:', err);
      setError('Failed to delete everything');
    }
  };

  // ── Notes handlers ──────────────────────────────────────────────────────────
  const handleAddNote = async (noteData: { title: string; content: string; color: string }) => {
    try {
      if (editingNote) {
        const updated = await notesApi.updateNote(editingNote._id!, noteData);
        setNotes(notes.map(n => n._id === editingNote._id ? updated : n));
        setEditingNote(null);
      } else {
        const created = await notesApi.createNote(noteData as any);
        setNotes([created, ...notes]);
      }
      setShowCaptureNoteModal(false);
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save note');
    }
  };

  const handleEditNote = (noteId: string) => {
    const note = notes.find(n => n._id === noteId);
    if (note) {
      setEditingNote(note);
      setShowCaptureNoteModal(true);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await notesApi.deleteNote(noteId);
      setNotes(notes.filter(n => n._id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      setError('Failed to delete note');
    }
  };

  // ── Client-side filter/sort (priority filter + sort — no backend change) ───
  const visibleIdeas = useMemo(() => {
    let list = filterPriority !== 'All' ? ideas.filter(i => i.priority === filterPriority) : ideas;
    if (sortBy === 'oldest') {
      list = [...list].reverse();
    } else if (sortBy === 'priority') {
      list = [...list].sort((a, b) => (PRIORITY_WEIGHT[b.priority] || 0) - (PRIORITY_WEIGHT[a.priority] || 0));
    }
    return list;
  }, [ideas, filterPriority, sortBy]);

  return (
    <div style={{ minHeight: "100vh", background: T.page, fontFamily: "DM Sans, system-ui, sans-serif", padding: "28px 32px 60px" }}>
      <PageTracker pageTitle="Idea Capture" category="project-management" />
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div style={{ fontSize: 22, fontWeight: 600, color: T.ink, marginBottom: 4 }}>Ideas & Notes</div>
            <p className="text-[color:var(--lyra-color-text-muted)] text-sm">
              Capture thoughts, develop ideas, and turn the useful ones into work.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              className="bg-[color:var(--lyra-color-surface-2)] text-[color:var(--app-text-2)] px-3 py-2 rounded-lg flex items-center space-x-2 hover:bg-[color:var(--app-surface-3)] text-sm font-semibold"
              title="Refresh all data"
            >
              <RefreshCw size={15} />
            </button>

            {/* + Capture split button */}
            <div className="relative inline-flex">
              <button
                onClick={() => activeTab === 'ideas' ? setShowCaptureModal(true) : setShowCaptureNoteModal(true)}
                className="text-white px-4 py-2 rounded-l-lg flex items-center gap-2 text-sm font-semibold transition-colors"
                style={{ background: T.accent }}
                onMouseEnter={e => (e.currentTarget.style.background = T.accentDark)}
                onMouseLeave={e => (e.currentTarget.style.background = T.accent)}
              >
                <Plus size={16} />
                Capture
              </button>
              <button
                onClick={() => setShowCaptureDropdown(v => !v)}
                className="text-white px-2 py-2 rounded-r-lg border-l border-[#2A5245] transition-colors"
                style={{ background: T.accent }}
                onMouseEnter={e => (e.currentTarget.style.background = T.accentDark)}
                onMouseLeave={e => (e.currentTarget.style.background = T.accent)}
              >
                <ChevronDown size={14} />
              </button>
              {showCaptureDropdown && (
                <>
                  <div onClick={() => setShowCaptureDropdown(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-[color:var(--lyra-color-border)] py-1 w-36" style={{ zIndex: 41 }}>
                    <button
                      onClick={() => { setEditingIdea(null); setShowCaptureModal(true); setShowCaptureDropdown(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-[color:var(--app-text-2)] hover:bg-[color:var(--lyra-color-surface)] flex items-center gap-2"
                    >
                      <Lightbulb size={13} /> New Idea
                    </button>
                    <button
                      onClick={() => { setEditingNote(null); setShowCaptureNoteModal(true); setShowCaptureDropdown(false); }}
                      className="w-full px-3 py-2 text-left text-sm text-[color:var(--app-text-2)] hover:bg-[color:var(--lyra-color-surface)] flex items-center gap-2"
                    >
                      <FileText size={13} /> New Note
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Stats — existing, working data, kept as-is */}
        <div className="flex items-center flex-wrap gap-x-5 gap-y-2 text-sm text-[color:var(--lyra-color-text-muted)] bg-white border border-[color:var(--lyra-color-border)] px-4 py-3 rounded-xl mb-4">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span>{stats.total} Total</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>{stats.inProgress} In Progress</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span>{stats.highPriority} High Priority</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
            <span>{stats.favorites} Favorites</span>
          </div>
        </div>

        {/* Ideas / Notes tab toggle */}
        <div className="inline-flex border border-[color:var(--lyra-color-border)] rounded-lg p-1 bg-white">
          <button
            onClick={() => setActiveTab('ideas')}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-semibold transition-colors"
            style={{ background: activeTab === 'ideas' ? T.accentLight : 'transparent', color: activeTab === 'ideas' ? T.accent : T.ink3 }}
          >
            <Lightbulb size={14} /> Ideas
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-semibold transition-colors"
            style={{ background: activeTab === 'notes' ? T.accentLight : 'transparent', color: activeTab === 'notes' ? T.accent : T.ink3 }}
          >
            <FileText size={14} /> Notes
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {activeTab === 'ideas' ? (
        <>
          {/* Toolbar */}
          <div className="mb-5 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-xs">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[color:var(--app-text-3)]" size={16} />
              <input
                type="text"
                placeholder="Search ideas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-[color:var(--app-border-strong)] rounded-lg text-sm focus:ring-2 focus:ring-[color:var(--lyra-color-primary)] focus:border-[color:var(--lyra-color-primary)]"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="border border-[color:var(--app-border-strong)] rounded-lg px-3 py-2 text-sm text-[color:var(--app-text-2)]"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category === 'All' ? 'All categories' : category}</option>
                ))}
              </select>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-[color:var(--app-border-strong)] rounded-lg px-3 py-2 text-sm text-[color:var(--app-text-2)]"
              >
                {statuses.map(status => (
                  <option key={status} value={status}>{status === 'All' ? 'All status' : status}</option>
                ))}
              </select>

              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="border border-[color:var(--app-border-strong)] rounded-lg px-3 py-2 text-sm text-[color:var(--app-text-2)]"
              >
                {priorities.map(p => (
                  <option key={p} value={p}>{p === 'All' ? 'All priority' : p}</option>
                ))}
              </select>

              <div className="flex border border-[color:var(--app-border-strong)] rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className="p-2"
                  style={{ background: viewMode === 'grid' ? T.surface2 : 'var(--lyra-color-surface)', color: viewMode === 'grid' ? T.accent : T.ink3 }}
                  title="Grid view"
                >
                  <LayoutGrid size={15} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className="p-2 border-l border-[color:var(--app-border-strong)]"
                  style={{ background: viewMode === 'list' ? T.surface2 : 'var(--lyra-color-surface)', color: viewMode === 'list' ? T.accent : T.ink3 }}
                  title="List view"
                >
                  <List size={15} />
                </button>
              </div>
            </div>
          </div>

          <div className="mb-3 flex items-center justify-between text-sm">
            <span style={{ color: T.ink3 }}>{visibleIdeas.length} idea{visibleIdeas.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-2" style={{ color: T.ink3 }}>
              Sort by:
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="border-none bg-transparent font-semibold text-sm"
                style={{ color: T.ink }}
              >
                <option value="latest">Latest</option>
                <option value="oldest">Oldest</option>
                <option value="priority">Priority</option>
              </select>
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--lyra-color-primary)]"></div>
              <span className="ml-2 text-[color:var(--lyra-color-text-muted)]">Loading ideas...</span>
            </div>
          ) : (
            <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" : "flex flex-col gap-3"}>
              {visibleIdeas.map((idea) => {
                const projectStatus = projectStatuses[idea._id!];
                return (
                  <FunnelCard
                    key={idea._id}
                    id={idea._id!}
                    title={idea.title}
                    description={idea.description}
                    category={idea.category}
                    status={idea.status}
                    priority={idea.priority as 'High' | 'Medium' | 'Low'}
                    tags={idea.tags}
                    isFavorite={idea.isFavorite}
                    currentStage="idea"
                    nextStage="project"
                    onPromote={handlePromoteIdea}
                    onToggleFavorite={toggleFavorite}
                    onEdit={handleEditIdea}
                    onDelete={handleDeleteIdea}
                    onDeleteIdeaOnly={handleDeleteIdeaOnly}
                    onDeleteIdeaAndProjects={handleDeleteIdeaAndProjects}
                    onDeleteEverything={handleDeleteEverything}
                    createdAt={idea.createdAt}
                    hasProject={projectStatus?.hasProject}
                    projectId={projectStatus?.projectId}
                    projectName={projectStatus?.projectName}
                    onGoToProject={handleGoToProject}
                  />
                );
              })}

              {visibleIdeas.length === 0 && (
                <div className="col-span-full bg-[color:var(--lyra-color-surface)] rounded-lg border-2 border-dashed border-[color:var(--app-border-strong)] p-12 text-center text-[color:var(--app-text-3)]">
                  <Lightbulb size={48} className="mx-auto mb-4 text-[color:var(--app-text-3)]" />
                  <h3 className="text-lg font-medium mb-2">No ideas found</h3>
                  <p className="text-sm">Try adjusting your filters or search terms, or capture a new idea!</p>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Notes tab */}
          <div className="mb-5 relative w-full lg:max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[color:var(--app-text-3)]" size={16} />
            <input
              type="text"
              placeholder="Search notes..."
              value={noteSearchTerm}
              onChange={(e) => setNoteSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[color:var(--app-border-strong)] rounded-lg text-sm focus:ring-2 focus:ring-[color:var(--lyra-color-primary)] focus:border-[color:var(--lyra-color-primary)]"
            />
          </div>

          <div className="mb-3 text-sm" style={{ color: T.ink3 }}>{notes.length} note{notes.length !== 1 ? 's' : ''}</div>

          {loadingNotes ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[color:var(--lyra-color-primary)]"></div>
              <span className="ml-2 text-[color:var(--lyra-color-text-muted)]">Loading notes...</span>
            </div>
          ) : notes.length === 0 ? (
            <div className="bg-[color:var(--lyra-color-surface)] rounded-lg border-2 border-dashed border-[color:var(--app-border-strong)] p-12 text-center text-[color:var(--app-text-3)]">
              <FileText size={48} className="mx-auto mb-4 text-[color:var(--app-text-3)]" />
              <h3 className="text-lg font-medium mb-2">No notes yet</h3>
              <p className="text-sm">Capture a quick thought or reference note.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {notes.map(note => {
                const c = NOTE_COLORS[note.color || 'yellow'];
                return (
                  <div
                    key={note._id}
                    onClick={() => handleEditNote(note._id!)}
                    className="rounded-xl p-4 cursor-pointer relative group"
                    style={{ background: c.bg, border: `1px solid ${c.border}` }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <FileText size={15} style={{ color: T.ink3 }} />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteNote(note._id!); }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: T.ink3 }}
                      >
                        <X size={13} />
                      </button>
                    </div>
                    <div className="text-sm font-semibold mb-1" style={{ color: T.ink }}>{note.title}</div>
                    <div className="text-xs mb-3 overflow-hidden" style={{ color: T.ink3, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>
                      {note.content}
                    </div>
                    {note.createdAt && (
                      <div className="text-[10px]" style={{ color: T.ink4 }}>
                        {new Date(note.createdAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Capture Idea Modal */}
      <CaptureIdeaModal
        isOpen={showCaptureModal}
        onClose={() => {
          setShowCaptureModal(false);
          setEditingIdea(null);
        }}
        onAdd={handleAddIdea}
        initialData={editingIdea}
      />

      {/* Capture Note Modal */}
      <CaptureNoteModal
        isOpen={showCaptureNoteModal}
        onClose={() => {
          setShowCaptureNoteModal(false);
          setEditingNote(null);
        }}
        onAdd={handleAddNote}
        initialData={editingNote}
      />

      {/* Promotion Modal */}
      {promotionItem && (
        <PromotionModal
          isOpen={showPromotionModal}
          onClose={() => setShowPromotionModal(false)}
          sourceItem={promotionItem}
          fromStage="idea"
          toStage="project"
          onConfirm={handleConfirmPromotion}
        />
      )}
    </div>
  );
};

export default IdeaCapture;
